// DraftManager.jsx - UI component for managing drafts
import React, { useState, useEffect } from 'react';
import {
  Save, FileText, Clock, User, MapPin, Languages,
  Eye, Edit, Trash2, Upload, CheckCircle, AlertCircle,
  XCircle, RefreshCw, Search, Filter
} from 'lucide-react';
import draftDatabase from './utils/draftDatabase';
import draftUtils from './utils/draftUtils';

const DraftManager = ({ onLoadDraft, onCreateNew, currentUserId = 'anonymous' }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [publishingDraft, setPublishingDraft] = useState(null);

  // Load drafts on component mount
  useEffect(() => {
    loadDrafts();
  }, [statusFilter]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const fetchedDrafts = await draftDatabase.getAllDrafts(filters);
      setDrafts(fetchedDrafts);

    } catch (err) {
      console.error('Error loading drafts:', err);
      setError('Failed to load drafts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId, storageUrl) => {
    try {
      // Delete from database
      await draftDatabase.deleteDraftRecord(draftId);

      // Delete from storage (if needed)
      if (storageUrl) {
        await draftUtils.deleteDraftFromStorage(storageUrl);
      }

      // Refresh the list
      await loadDrafts();
      setShowDeleteConfirm(null);

    } catch (err) {
      console.error('Error deleting draft:', err);
      setError('Failed to delete draft: ' + err.message);
    }
  };

  const handlePublishDraft = async (draft) => {
    try {
      setPublishingDraft(draft.id);
      setError('');

      // Load draft data from storage
      const draftData = await draftUtils.loadDraftFromStorage(draft.storageUrl);

      // Publish to live location
      const publishResult = await draftUtils.publishDraftToLive(draft, draftData);

      // Update database record
      await draftDatabase.markDraftAsPublished(draft.id, publishResult.publicUrl, currentUserId);

      // Refresh the list
      await loadDrafts();

    } catch (err) {
      console.error('Error publishing draft:', err);
      setError('Failed to publish draft: ' + err.message);
    } finally {
      setPublishingDraft(null);
    }
  };

  const handleLoadDraft = async (draft) => {
    try {
      const draftData = await draftUtils.loadDraftFromStorage(draft.storageUrl);
      onLoadDraft(draftData, draft);
    } catch (err) {
      console.error('Error loading draft:', err);
      setError('Failed to load draft: ' + err.message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4 text-gray-500" />;
      case 'under_review': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'published': return <Upload className="w-4 h-4 text-purple-500" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'under_review': return 'bg-blue-100 text-blue-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'published': return 'bg-purple-100 text-purple-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const filteredDrafts = drafts.filter(draft => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        draft.title?.toLowerCase().includes(searchLower) ||
        draft.country?.toLowerCase().includes(searchLower) ||
        draft.city?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Draft Manager</h2>
          <p className="text-gray-600">Manage your guide drafts and review process</p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <FileText className="w-4 h-4" />
          Create New Guide
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search drafts by title, country, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
            </select>
          </div>
          <button
            onClick={loadDrafts}
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">Error</span>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading drafts...</span>
        </div>
      ) : (
        /* Drafts Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDrafts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No drafts found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first guide to get started'
                }
              </p>
            </div>
          ) : (
            filteredDrafts.map((draft) => (
              <div key={draft.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                {/* Draft Header */}
                <div className="p-4 border-b">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-800 truncate flex-1">
                      {draft.title || 'Untitled Guide'}
                    </h3>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(draft.status)}`}>
                      {getStatusIcon(draft.status)}
                      {draft.status.replace('_', ' ')}
                    </div>
                  </div>

                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>{draft.country}{draft.city ? `, ${draft.city}` : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Languages className="w-3 h-3" />
                      <span>{draft.language || 'en'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{draft.estimatedDuration || 0} minutes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>{draft.createdBy}</span>
                    </div>
                  </div>
                </div>

                {/* Draft Content */}
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {draft.description || 'No description available'}
                  </p>

                  <div className="text-xs text-gray-500 mb-4">
                    <div>Created: {new Date(draft.createdAt).toLocaleDateString()}</div>
                    <div>Modified: {new Date(draft.lastModified).toLocaleDateString()}</div>
                    <div>{draft.pointsCount || 0} POIs</div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoadDraft(draft)}
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-1 text-sm"
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>

                    {draft.status === 'approved' && (
                      <button
                        onClick={() => handlePublishDraft(draft)}
                        disabled={publishingDraft === draft.id}
                        className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 flex items-center justify-center gap-1 text-sm"
                      >
                        {publishingDraft === draft.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Upload className="w-3 h-3" />
                        )}
                        Publish
                      </button>
                    )}

                    <button
                      onClick={() => setShowDeleteConfirm(draft)}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center text-sm"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <XCircle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold">Delete Draft</h3>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{showDeleteConfirm.title}"? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteDraft(showDeleteConfirm.id, showDeleteConfirm.storageUrl)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftManager;